import { assign, fromCallback, setup } from 'xstate';
import type { AnyActorRef } from 'xstate';
import { DateTime } from 'luxon';

import reservationUtils from '@/services/reservationUtils';

import type { TripArrayFieldContext } from './createTripArrayFieldMachine';
import type { WillCallContext } from './willCall.machine';

export type AppointmentTimeAllowedForPortalInput = {
  appointmentDateRef: AnyActorRef;
  appointmentDateTimeRef: AnyActorRef;
  willCallRef: AnyActorRef;
};

export type AppointmentTimeAllowedForPortalContext = {
  appointmentDateRef: AnyActorRef;
  appointmentDateTimeRef: AnyActorRef;
  willCallRef: AnyActorRef;
  /** `null` disables the check (non-member portal or unknown portal). */
  portalName: string | null;
  isAllowedByIndex: boolean[];
};

export type AppointmentTimeAllowedForPortalEvent =
  | { type: 'APPOINTMENT_DATETIMES_UPDATED' }
  | { type: 'SET_PORTAL_NAME'; portalName: string | null }
  | { type: 'RESET' };

function readTripArrayValues(ref: AnyActorRef): unknown[] {
  return (ref.getSnapshot().context as TripArrayFieldContext).values;
}

/**
 * The appointment value to check for each trip leg. Will-call legs only have
 * an appointment date, timed legs an appointment date-time. See: F1V2-6841
 */
function readAppointmentValues(
  context: AppointmentTimeAllowedForPortalInput,
): unknown[] {
  const dates = readTripArrayValues(context.appointmentDateRef);
  const dateTimes = readTripArrayValues(context.appointmentDateTimeRef);
  const willCalls = (
    context.willCallRef.getSnapshot().context as WillCallContext
  ).values;
  const count = Math.max(1, dateTimes.length);
  return Array.from({ length: count }, (_, index) =>
    willCalls[index] ? dates[index] : dateTimes[index],
  );
}

function toDateTime(value: unknown): DateTime | null {
  if (value instanceof DateTime) {
    return value.isValid ? value : null;
  }
  if (typeof value === 'string' && value) {
    const dt = DateTime.fromISO(value, { setZone: true });
    return dt.isValid ? dt : null;
  }
  return null;
}

function computeIsAllowed(
  values: unknown[],
  portalName: string | null,
): boolean[] {
  return values.map((value) =>
    reservationUtils.isAppointmentTimeAllowedForPortal(
      toDateTime(value),
      portalName,
    ),
  );
}

const appointmentDateTimeSubscription = fromCallback(
  ({
    sendBack,
    input,
  }: {
    sendBack: (event: { type: 'APPOINTMENT_DATETIMES_UPDATED' }) => void;
    input: AppointmentTimeAllowedForPortalInput;
  }) => {
    const emit = () => sendBack({ type: 'APPOINTMENT_DATETIMES_UPDATED' });
    const subs = [
      input.appointmentDateRef.subscribe(emit),
      input.appointmentDateTimeRef.subscribe(emit),
      input.willCallRef.subscribe(emit),
    ];
    emit();
    return () => subs.forEach((sub) => sub.unsubscribe());
  },
);

export const appointmentTimeAllowedForPortalMachine = setup({
  types: {
    context: {} as AppointmentTimeAllowedForPortalContext,
    events: {} as AppointmentTimeAllowedForPortalEvent,
    input: {} as AppointmentTimeAllowedForPortalInput,
  },
  actors: {
    appointmentDateTimeSubscription,
  },
}).createMachine({
  id: 'appointmentTimeAllowedForPortal',
  context: ({ input }) => ({
    appointmentDateRef: input.appointmentDateRef,
    appointmentDateTimeRef: input.appointmentDateTimeRef,
    willCallRef: input.willCallRef,
    portalName: null,
    isAllowedByIndex: [true],
  }),
  invoke: {
    id: 'appointmentDateTimeSubscription',
    src: 'appointmentDateTimeSubscription',
    input: ({ context }) => ({
      appointmentDateRef: context.appointmentDateRef,
      appointmentDateTimeRef: context.appointmentDateTimeRef,
      willCallRef: context.willCallRef,
    }),
  },
  on: {
    APPOINTMENT_DATETIMES_UPDATED: {
      actions: assign(({ context }) => {
        const values = readAppointmentValues(context);
        return {
          isAllowedByIndex: computeIsAllowed(values, context.portalName),
        };
      }),
    },
    SET_PORTAL_NAME: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'SET_PORTAL_NAME') return {};
        const values = readAppointmentValues(context);
        return {
          portalName: event.portalName,
          isAllowedByIndex: computeIsAllowed(values, event.portalName),
        };
      }),
    },
    RESET: {
      actions: assign({
        isAllowedByIndex: () => [true],
      }),
    },
  },
});
